import { api } from '../api/client.js';
import { isOtpLockedOut } from './action-denied.js';
import { requiresTroubleshootingConfirmation } from './skip-2fa-reasons.js';

/**
 * Result of {@link commitHighSecurity}. `ok` is `true` only when the server
 * recorded `high`. `lockedOut` distinguishes the ONE terminal-OTP-lockout
 * refusal (CCF-16/#639) — a 403 `{ error: 'otp_locked_out' }` from either the
 * `/security/high/skip-2fa` or `/security/high/complete` step — from every other
 * failure, so the caller can surface the dedicated 24h-lockout copy and show 2FA
 * as FAILED (issue #793) rather than the generic "could not be recorded" error.
 */
export interface CommitHighSecurityResult {
  ok: boolean;
  lockedOut: boolean;
}

/**
 * Begin a server-side **High Security run** for the identified customer (issue
 * #363). The High Security screens call this when the high-security flow starts
 * (once the customer is identified — the run may begin before `medium`, since on
 * the HIGH variants the high-flow 2FA-OTP is itself what raises the session to
 * `medium`). It tells the BFF to record a session-bound, customer-scoped run so
 * that ONLY the 2FA step performed within this run can later satisfy
 * `commitHighSecurity` — a bare medium ID&V session (or a forged request) cannot
 * elevate to `high` directly.
 *
 * Best-effort from the UI's perspective: the server gate still fail-closes at
 * completion if the run was not recorded, so a failure here is non-fatal to the
 * UX flow (the agent can still work the questions) but WILL block completion.
 *
 * `variant` forwards the high-security screen's identifier (the RBAC screen id,
 * e.g. `SCRN-06`) so the BFF can decide — from its OWN server-side allow-list of
 * no-2FA-by-design variants — whether to record a **no-2FA high run** for SCRN-06
 * Authorised VISA User (CCF-25 — verified against TSYS with no OTP by design,
 * issue #554). The no-2FA decision is the SERVER's: the client never sends a
 * `requires2fa` boolean, so it cannot begin an arbitrary high run as no-2FA. Any
 * non-allow-listed / omitted variant begins a 2FA-bearing run that still requires
 * a server-observed OTP or a sanctioned skip, and the recorded fact is read only
 * from the server-stored run at completion — never from the completion call.
 *
 * @returns `true` when the server started the run; `false` otherwise.
 */
export async function beginHighSecurity(
  customerNumber: string,
  tellerId: string,
  variant?: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST('/security/high/begin', {
      params: { header: { 'X-Teller-Id': tellerId } },
      body: variant ? { customerNumber, variant } : { customerNumber },
    });
    return response.ok && data?.started === true;
  } catch {
    return false;
  }
}

/**
 * Carry a **same-call 2FA-OTP validation** onto the active High Security run
 * (issue #456 — @dylan219's #412 rule for the medium→high crossover).
 *
 * When the customer completed the SMS OTP earlier on THIS call (on a medium /
 * ID&V page) and the agent then routes to a High Security page, the High run's
 * server-side 2FA step must be satisfied so the agent does not redo the OTP and
 * `commitHighSecurity` can succeed. The client MUST NOT merely hide the OTP UI
 * on a restored client-side `otpStatus:'pass'` — that leaves the server run's
 * 2FA UNSATISFIED (`/security/high/complete` would still 403). This asks the BFF
 * to record the run's 2FA step from the **server-observed** same-call OTP.
 *
 * The BFF fail-closes: it records the run's 2FA marker ONLY when an active High
 * Security run exists for the identified customer AND the session carries a
 * server-observed same-call OTP validation for that customer. A client-supplied
 * status is never trusted. Idempotent.
 *
 * @returns `true` when the server recorded the run's 2FA step; `false` otherwise
 *   (403 / network) — the caller must then KEEP the OTP/skip UI visible so the
 *   agent can satisfy 2FA the ordinary way (fail closed).
 */
export async function carryHighSecurity2fa(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST('/security/high/carry-2fa', {
      params: { header: { 'X-Teller-Id': tellerId } },
      body: { customerNumber },
    });
    return response.ok && data?.carried === true;
  } catch {
    return false;
  }
}

/**
 * Commit **High Security** completion for the identified customer server-side
 * (issue #363).
 *
 * The High Security screens (SCRN 4..10 high variants / EDS High) already flip
 * the *client* customer context to `securityLevel: 'high'` on completion, which
 * drives the UX (unlocks the dashboard, satisfies the SCRN-26 screen-access
 * affordance). That client flag is NOT a security boundary — it can be tampered
 * with. This call tells the BFF to record `high` on the per-interaction,
 * session-bound verification context, which is what actually gates the
 * high-security servicing write (`POST /api/customers/contact-number`, the
 * Mobile Number maintenance screen) via `requireVerifiedHigh`.
 *
 * The BFF raises the level only for the customer the session is already bound to
 * (via ID&V), only if that customer has already reached `medium`, and only when
 * the High Security 2FA step was actually satisfied server-side — EITHER a
 * server-mediated OTP validation for this customer OR a sanctioned 2FA SKIP with
 * a reason (CCF-107 / #287). The skip is NOT trusted as a body string on this
 * call: when a `skip2faReason` is supplied it is first recorded server-side via
 * `/security/high/skip-2fa` (bound to the active run + reason-validated), and
 * only then is completion committed. A failed or absent ID&V, or a bare `medium`
 * session with no High Security run, cannot use this to reach `high` (the BFF
 * returns 403).
 *
 * Best-effort from the UI's perspective: a failure is surfaced to the caller so
 * it can decide (the server gate still fail-closes on the subsequent write if
 * this did not land), but it must not silently be treated as success.
 *
 * @returns a {@link CommitHighSecurityResult}: `ok` is `true` when the server
 * recorded `high`, and `lockedOut` flags the terminal OTP-lockout refusal so the
 * caller can show the CCF-16 24h-lockout message + a FAILED 2FA (issue #793).
 */
export async function commitHighSecurity(
  customerNumber: string,
  tellerId: string,
  skip2faReason?: string,
  noTwoFactor?: boolean,
  teamLeadName?: string,
  troubleshootingCompleted?: boolean,
): Promise<CommitHighSecurityResult> {
  try {
    // Sanctioned 2FA skip (CCF-107 / #287): record it server-side FIRST so the
    // completion below can consume the server-stored marker. If the skip is not
    // recorded (403/400/network), completion will fail closed — surface that.
    if (skip2faReason) {
      // #586: when the reason is "2FA Exemption Approved by TL" the approving
      // team lead's name is captured in the audited skip record. Forwarded only
      // when supplied; the server independently requires + validates it for that
      // reason (never trusts the client).
      // #789: when the reason is "2FA Customer Device Issue" the agent's
      // "Troubleshooting completed" attestation is forwarded so the server can
      // fail-closed require it (the server never trusts the client — the
      // client-side checkbox is UX only).
      const {
        data: skipData,
        error: skipError,
        response: skipResponse,
      } = await api.POST('/security/high/skip-2fa', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          reason: skip2faReason,
          ...(teamLeadName ? { teamLeadName } : {}),
          ...(requiresTroubleshootingConfirmation(skip2faReason)
            ? { troubleshootingCompleted: troubleshootingCompleted === true }
            : {}),
        },
      });
      if (!skipResponse.ok || skipData?.recorded !== true) {
        // #793 — a terminal OTP lockout (CCF-16/#639) fails the skip closed with
        // 403 `{ error: 'otp_locked_out' }`. Flag it so the caller surfaces the
        // dedicated 24h-lockout copy + shows 2FA as FAILED, not the generic
        // "could not be recorded" error. The lockout stays enforced server-side.
        return {
          ok: false,
          lockedOut: isOtpLockedOut(skipResponse.status, skipError),
        };
      }
    } else if (noTwoFactor) {
      // No-2FA high variant (SCRN-06 Authorised VISA User / CCF-25, issue #554):
      // there is no OTP, so record the completed identity checks server-side
      // FIRST via a DISTINCT step so that completion has evidence beyond `begin`
      // alone (ai-review). If this is not recorded (403/network), completion
      // fails closed — surface that. This is NOT a 2FA bypass: the server only
      // accepts it for an active run it itself recorded as no-2FA at begin.
      const { data: rcData, response: rcResponse } = await api.POST(
        '/security/high/record-checks',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        },
      );
      if (!rcResponse.ok || rcData?.recorded !== true) {
        return { ok: false, lockedOut: false };
      }
    }
    const { data, error, response } = await api.POST(
      '/security/high/complete',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      },
    );
    if (response.ok && data?.level === 'high') {
      return { ok: true, lockedOut: false };
    }
    // #793 — `/security/high/complete` also fails closed on the terminal OTP
    // lockout (403 `{ error: 'otp_locked_out' }`); surface it the same way.
    return { ok: false, lockedOut: isOtpLockedOut(response.status, error) };
  } catch {
    return { ok: false, lockedOut: false };
  }
}
